import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, KeyboardEvent, RefObject } from "react";
import { ChatIcon, CloseIcon, CopyIcon, ExternalIcon, HistoryIcon, PlusIcon } from "../../components/Icons";
import type { LearningCatalog, LearningChunk } from "../../types";
import { chatbotConfig } from "../config";
import { buildChatbotResponse } from "../engine/buildResponse";
import { GUIDED_FLOW_TRIGGER, buildObjectiveRecommendations, createGuidedPrompt, getGuidanceResources, parseObjectiveCodes } from "../engine/guidedFlow";
import type {
  ChatMessage,
  ChatSession,
  ChatbotData,
  ChatbotIntent,
  QueryProfiles,
  ChatbotResponse,
  ChatbotTopic,
  EdTechProductType,
  GuidedFlowPromptMessage,
  StartupChallenge,
  StartupStage,
  ResourceRecommendation,
  SourceSnippet
} from "../types";
import { logChatEvent } from "../telemetry/logChatEvent";

const CHUNKS_URL = "/data/learning-chunks.json";
const TOPICS_URL = "/data/chatbot/chatbot-topics.json";
const INTENTS_URL = "/data/chatbot/chatbot-intents.json";
const CANNED_RESPONSES_URL = "/data/chatbot/chatbot-canned-responses.json";
const QUERY_PROFILES_URL = "/data/chatbot/chatbot-query-profiles.json";
const STORAGE_KEY = "learning-guide-chat-sessions";
const MAX_SESSIONS = 10;
const MAX_SESSION_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const PENDING_STATUSES = ["Reading the library...", "Checking key objectives and enablers...", "Selecting the best matches..."];

type ChatWidgetProps = {
  catalog: LearningCatalog;
};

export function ChatWidget({ catalog }: ChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [data, setData] = useState<ChatbotData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>(() => (chatbotConfig.allowHistory ? loadSessions() : [createSession()]));
  const [activeSessionId, setActiveSessionId] = useState(() => sessions[0]?.id ?? createSession().id);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [expandedResponseIds, setExpandedResponseIds] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement | null>(null);
  const latestExchangeRef = useRef<HTMLDivElement | null>(null);
  const pendingTimerRef = useRef<number | null>(null);

  const activeSession = useMemo(() => {
    const existing = sessions.find((session) => session.id === activeSessionId);
    return existing ?? sessions[0] ?? createSession(activeSessionId);
  }, [activeSessionId, sessions]);
  const isObjectiveCodeStep = activeSession.activeFlow?.id === "get_guidance" && activeSession.activeFlow.step === "objectiveCodes";
  const canUseFreeText = chatbotConfig.allowFreeText && Boolean(data);
  const canUseChatInput = canUseFreeText || isObjectiveCodeStep;
  const starterGroups = useMemo(() => getStarterGroups(), []);

  useEffect(() => {
    if (sessions.length === 0) {
      const session = createSession();
      setSessions([session]);
      setActiveSessionId(session.id);
    }
  }, [sessions.length]);

  useEffect(() => {
    if (chatbotConfig.allowHistory) {
      saveSessions(sessions);
    }
  }, [sessions]);

  useEffect(() => {
    let ignore = false;

    async function loadChatbotData() {
      try {
        const [chunksResponse, topicsResponse, intentsResponse, cannedResponse, queryProfilesResponse] = await Promise.all([
          fetch(CHUNKS_URL),
          fetch(TOPICS_URL),
          fetch(INTENTS_URL),
          fetch(CANNED_RESPONSES_URL),
          fetch(QUERY_PROFILES_URL)
        ]);
        for (const response of [chunksResponse, topicsResponse, intentsResponse, cannedResponse, queryProfilesResponse]) {
          if (!response.ok) {
            throw new Error(`Failed to load ${response.url}: ${response.status}`);
          }
        }

        const loadedData: ChatbotData = {
          chunks: (await chunksResponse.json()) as LearningChunk[],
          topics: (await topicsResponse.json()) as ChatbotTopic[],
          intents: (await intentsResponse.json()) as ChatbotIntent[],
          cannedResponses: (await cannedResponse.json()) as Record<string, string>,
          queryProfiles: (await queryProfilesResponse.json()) as QueryProfiles
        };

        if (!ignore) {
          setData(loadedData);
          setLoadError(null);
        }
      } catch (caught) {
        if (!ignore) {
          setLoadError(caught instanceof Error ? caught.message : "Failed to load chatbot data.");
        }
      }
    }

    if (chatbotConfig.enableChatbot && chatbotConfig.allowFreeText) {
      loadChatbotData();
    }

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen, activeSessionId]);

  useEffect(() => {
    return () => {
      if (pendingTimerRef.current) {
        window.clearTimeout(pendingTimerRef.current);
      }
    };
  }, []);

  if (!chatbotConfig.enableChatbot) {
    return null;
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submitQuery(input);
  };

  const submitQuery = (value: string) => {
    const query = value.trim();
    if (!query || isPending) {
      return;
    }

    if (query === GUIDED_FLOW_TRIGGER) {
      if (chatbotConfig.allowGuidedFlow) {
        startGuidedFlow();
      }
      return;
    }

    if (activeSession.activeFlow?.id === "get_guidance" && activeSession.activeFlow.step === "objectiveCodes") {
      submitObjectiveCodes(query);
      return;
    }

    if (!chatbotConfig.allowFreeText || !data) {
      return;
    }

    const chatbotData = data;

    const now = new Date().toISOString();
    const pendingId = `pending-${Date.now()}`;
    const pendingMessage: ChatMessage = {
      id: pendingId,
      role: "pending",
      statusText: PENDING_STATUSES[0],
      createdAt: now
    };
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: query,
      createdAt: now
    };

    setInput("");
    setIsPending(true);
    updateActiveSession((session) => {
      const title = session.messages.length === 0 ? truncateTitle(query) : session.title;
      return {
        ...session,
        title,
        updatedAt: now,
        messages: [...session.messages, userMessage, pendingMessage]
      };
    });

    const statusStepMs = Math.max(180, Math.floor(chatbotConfig.minResponseDelayMs / PENDING_STATUSES.length));
    PENDING_STATUSES.slice(1).forEach((statusText, index) => {
      window.setTimeout(() => {
        updatePendingMessage(pendingId, statusText);
      }, statusStepMs * (index + 1));
    });

    const delay = randomDelay(chatbotConfig.minResponseDelayMs, chatbotConfig.maxResponseDelayMs);
    pendingTimerRef.current = window.setTimeout(() => {
      const response = buildChatbotResponse(catalog, chatbotData, query);
      const botMessage: ChatMessage = {
        id: response.id,
        role: "bot",
        response,
        createdAt: new Date().toISOString()
      };
      updateActiveSession((session) => ({
        ...session,
        updatedAt: botMessage.createdAt,
        messages: session.messages.map((message) => (message.id === pendingId ? botMessage : message))
      }));
      setIsPending(false);
      scrollLatestExchangeIntoView(latestExchangeRef);
    }, delay);
  };

  const startNewChat = () => {
    const session = createSession();
    setSessions((current) => trimSessions([session, ...current]));
    setActiveSessionId(session.id);
    setIsHistoryOpen(false);
    setIsPending(false);
  };

  const clearHistory = () => {
    const session = createSession();
    window.localStorage.removeItem(STORAGE_KEY);
    setSessions([session]);
    setActiveSessionId(session.id);
    setIsHistoryOpen(false);
    setIsPending(false);
    setExpandedResponseIds(new Set());
  };

  const restoreSession = (sessionId: string) => {
    setActiveSessionId(sessionId);
    setIsHistoryOpen(false);
    setIsPending(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      if (isHistoryOpen) {
        setIsHistoryOpen(false);
      } else {
        setIsOpen(false);
      }
    }
  };

  const latestExchangeAnchor = latestExchangeAnchorId(activeSession.messages);

  return (
    <section
      className={isOpen ? "chat-widget chat-widget--open" : "chat-widget"}
      aria-label="Learning guide chatbot"
      onKeyDown={handleKeyDown}
    >
      {isOpen ? (
        <div className="chat-widget__panel">
          <div className="chat-widget__header">
            <div className="chat-widget__brand">
              <span className="chat-widget__avatar" aria-hidden="true">
                <ChatIcon />
              </span>
              <div>
                <h2>Learning guide</h2>
                <p>Find startup learning materials from the Navigator library.</p>
              </div>
            </div>
            <div className="chat-widget__actions">
              {chatbotConfig.allowHistory ? (
                <button type="button" onClick={() => setIsHistoryOpen((current) => !current)} aria-label="Show chat history">
                  <HistoryIcon />
                </button>
              ) : null}
              {chatbotConfig.allowFreeText || chatbotConfig.allowGuidedFlow ? (
                <button type="button" onClick={startNewChat} aria-label="Start a new chat">
                  <PlusIcon />
                </button>
              ) : null}
              <button type="button" onClick={() => setIsOpen(false)} aria-label="Close chatbot">
                <CloseIcon />
              </button>
            </div>
          </div>

          {isHistoryOpen && chatbotConfig.allowHistory ? (
            <div className="chat-history" aria-label="Chat history">
              <div className="chat-history__header">
                <strong>Recent chats</strong>
                <div className="chat-history__actions">
                  <button type="button" onClick={startNewChat}>
                    New chat
                  </button>
                  <button type="button" onClick={clearHistory}>
                    Clear history
                  </button>
                </div>
              </div>
              <div className="chat-history__list">
                {sessions.map((session) => (
                  <button
                    className={session.id === activeSessionId ? "chat-history__item chat-history__item--active" : "chat-history__item"}
                    key={session.id}
                    type="button"
                    onClick={() => restoreSession(session.id)}
                  >
                    <span>{session.title}</span>
                    <small>{formatRelativeDate(session.updatedAt)}</small>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="chat-widget__messages" aria-live="polite">
            {activeSession.messages.length === 0 ? (
              <div className="chat-widget__intro">
                <strong>Ask the learning guide to narrow the library.</strong>
                <p>The guide searches key objectives, enablers, summaries, and generated library chunks, then shows the most relevant resources.</p>
                <div className="chat-starters" aria-label="Starter prompts">
                  {starterGroups.map((starter) => (
                    <button
                      key={starter.label}
                      type="button"
                      onClick={() => submitQuery(starter.prompt)}
                      disabled={isPending || (starter.prompt !== GUIDED_FLOW_TRIGGER && !data)}
                    >
                      <span>{starter.label}</span>
                      <small>{starter.prompt}</small>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              activeSession.messages.map((message) => {
                if (message.role === "user") {
                  return (
                    <div className="chat-message chat-message--user" key={message.id} ref={message.id === latestExchangeAnchor ? latestExchangeRef : undefined}>
                      {message.text}
                    </div>
                  );
                }
                if (message.role === "pending") {
                  return <PendingMessage key={message.id} statusText={message.statusText} />;
                }
                if (message.role === "guided_prompt") {
                  return (
                    <GuidedPromptMessage
                      key={message.id}
                      message={message}
                      onSelect={(option) => handleGuidedSelection(message, option)}
                      isAnchor={message.id === latestExchangeAnchor}
                      latestExchangeRef={latestExchangeRef}
                    />
                  );
                }
                if (message.role === "guided_result") {
                  return (
                    <GuidedResultMessage
                      key={message.id}
                      message={message}
                      onAction={handleGuidedAction}
                      isAnchor={message.id === latestExchangeAnchor}
                      latestExchangeRef={latestExchangeRef}
                    />
                  );
                }

                return (
                  <BotMessage
                    key={message.id}
                    response={message.response}
                    onFollowUp={submitQuery}
                    isAnchor={message.id === latestExchangeAnchor}
                    latestExchangeRef={latestExchangeRef}
                    isExpanded={expandedResponseIds.has(message.response.id)}
                    onShowMore={() =>
                      setExpandedResponseIds((current) => {
                        const next = new Set(current);
                        next.add(message.response.id);
                        return next;
                      })
                    }
                  />
                );
              })
            )}
            {loadError ? <p className="chat-widget__error">{loadError}</p> : null}
          </div>

          {chatbotConfig.allowFreeText || isObjectiveCodeStep ? (
            <form className="chat-widget__form" onSubmit={handleSubmit}>
              <input
                ref={inputRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder={inputPlaceholder(activeSession, isPending, Boolean(data), chatbotConfig.allowFreeText)}
                disabled={!canUseChatInput || isPending}
                aria-label="Ask the learning guide"
              />
              <button type="submit" disabled={!canUseChatInput || !input.trim() || isPending}>
                {isPending ? "Checking" : "Send"}
              </button>
            </form>
          ) : null}
          <p className="chat-widget__caution">This guide searches the current Navigator library and may miss resources with thin metadata.</p>
        </div>
      ) : (
        <button className="chat-widget__launcher" type="button" onClick={() => setIsOpen(true)}>
          <ChatIcon />
          <span>Ask for help</span>
        </button>
      )}
    </section>
  );

  function updateActiveSession(updater: (session: ChatSession) => ChatSession) {
    setSessions((current) =>
      trimSessions(
        current.map((session) => (session.id === activeSessionId ? updater(session) : session))
      )
    );
  }

  function updatePendingMessage(messageId: string, statusText: string) {
    updateActiveSession((session) => ({
      ...session,
      messages: session.messages.map((message) => (message.id === messageId && message.role === "pending" ? { ...message, statusText } : message))
    }));
  }

  function startGuidedFlow() {
    if (!chatbotConfig.allowGuidedFlow) {
      return;
    }

    const now = new Date().toISOString();
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: GUIDED_FLOW_TRIGGER,
      createdAt: now
    };
    const prompt = createGuidedPrompt("challenge", {});
    updateActiveSession((session) => ({
      ...session,
      title: session.messages.length === 0 ? "Get guidance" : session.title,
      updatedAt: now,
      startupContext: {},
      activeFlow: { id: "get_guidance", step: "challenge" },
      messages: [...session.messages, userMessage, prompt]
    }));
    scrollLatestExchangeIntoView(latestExchangeRef);
  }

  function handleGuidedSelection(message: GuidedFlowPromptMessage, option: string) {
    const context = {
      ...activeSession.startupContext,
      ...(message.step === "challenge" ? { challenge: option as StartupChallenge } : {}),
      ...(message.step === "stage" ? { stage: option as StartupStage } : {}),
      ...(message.step === "productType" ? { productType: option as EdTechProductType } : {})
    };
    const now = new Date().toISOString();
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: option,
      createdAt: now
    };

    if (message.step === "challenge") {
      const prompt = createGuidedPrompt("stage", context);
      appendGuidedStep(context, "stage", userMessage, prompt);
      return;
    }

    if (message.step === "stage") {
      const prompt = createGuidedPrompt("productType", context);
      appendGuidedStep(context, "productType", userMessage, prompt);
      return;
    }

    if (message.step === "productType") {
      const prompt = createGuidedPrompt("assignedObjectives", context);
      appendGuidedStep(context, "assignedObjectives", userMessage, prompt);
      return;
    }

    if (message.step === "assignedObjectives") {
      if (option === "Skip") {
        appendGuidedResult(context, userMessage);
        return;
      }
      const prompt = createGuidedPrompt("objectiveCodes", context);
      appendGuidedStep(context, "objectiveCodes", userMessage, prompt);
      return;
    }
  }

  function submitObjectiveCodes(value: string) {
    const now = new Date().toISOString();
    const codes = parseObjectiveCodes(value, catalog);
    const context = {
      ...activeSession.startupContext,
      assignedObjectiveCodes: codes
    };
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: value,
      createdAt: now
    };
    setInput("");
    appendGuidedResult(context, userMessage);
  }

  function appendGuidedResult(context: ChatSession["startupContext"], userMessage: ChatMessage) {
    const now = new Date().toISOString();
    const resultMessage: ChatMessage = {
      id: `guided-result-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      role: "guided_result",
      flowId: "get_guidance",
      context: context ?? {},
      recommendations: buildObjectiveRecommendations(catalog, context ?? {}),
      createdAt: now
    };
    updateActiveSession((session) => ({
      ...session,
      updatedAt: now,
      startupContext: context,
      activeFlow: { id: "get_guidance", step: "results" },
      messages: [...session.messages, userMessage, resultMessage]
    }));
    scrollLatestExchangeIntoView(latestExchangeRef);
  }

  function appendGuidedStep(
    context: ChatSession["startupContext"],
    step: "stage" | "productType" | "assignedObjectives" | "objectiveCodes",
    userMessage: ChatMessage,
    prompt: ChatMessage
  ) {
    const now = new Date().toISOString();
    updateActiveSession((session) => ({
      ...session,
      updatedAt: now,
      startupContext: context,
      activeFlow: { id: "get_guidance", step },
      messages: [...session.messages, userMessage, prompt]
    }));
    scrollLatestExchangeIntoView(latestExchangeRef);
  }

  function handleGuidedAction(action: string) {
    if (action === "Show resources") {
      if (!chatbotConfig.allowFreeText) {
        return;
      }

      const context = activeSession.startupContext;
      const query = [
        context?.stage && context.stage !== "Not sure" ? context.stage : "",
        context?.productType && context.productType !== "Not sure" ? context.productType : "",
        context?.challenge && context.challenge !== "Something else" ? context.challenge : "startup resources",
        context?.assignedObjectiveCodes?.length ? context.assignedObjectiveCodes.join(" ") : ""
      ]
        .filter(Boolean)
        .join(" ");
      submitQuery(query);
      return;
    }
    if (action === "Change challenge") {
      const prompt = createGuidedPrompt("challenge", activeSession.startupContext ?? {});
      appendGuidedOnly(prompt, "challenge");
      return;
    }
    if (action === "Change stage") {
      const prompt = createGuidedPrompt("stage", activeSession.startupContext ?? {});
      appendGuidedOnly(prompt, "stage");
      return;
    }
    if (action === "Change product type") {
      const prompt = createGuidedPrompt("productType", activeSession.startupContext ?? {});
      appendGuidedOnly(prompt, "productType");
      return;
    }
    if (action === "Add assigned key objectives") {
      const prompt = createGuidedPrompt("objectiveCodes", activeSession.startupContext ?? {});
      appendGuidedOnly(prompt, "objectiveCodes");
      return;
    }
    if (action === "Start over") {
      startGuidedFlow();
    }
  }

  function appendGuidedOnly(prompt: ChatMessage, step: "challenge" | "stage" | "productType" | "assignedObjectives" | "objectiveCodes") {
    const now = new Date().toISOString();
    updateActiveSession((session) => ({
      ...session,
      updatedAt: now,
      activeFlow: { id: "get_guidance", step },
      messages: [...session.messages, prompt]
    }));
    scrollLatestExchangeIntoView(latestExchangeRef);
  }
}

function PendingMessage({ statusText }: { statusText: string }) {
  return (
    <div className="chat-message chat-message--bot chat-message--pending" role="status">
      <span className="chat-typing" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      <p>{statusText}</p>
    </div>
  );
}

function GuidedPromptMessage({
  message,
  onSelect,
  isAnchor,
  latestExchangeRef
}: {
  message: GuidedFlowPromptMessage;
  onSelect: (option: string) => void;
  isAnchor: boolean;
  latestExchangeRef: RefObject<HTMLDivElement>;
}) {
  const options = message.options;

  return (
    <div className="chat-message chat-message--bot chat-guided" ref={isAnchor ? latestExchangeRef : undefined}>
      <div className="chat-response__meta">
        <span>Get guidance</span>
        <span>{guidedStepLabel(message.step)}</span>
      </div>
      <p>{message.question}</p>
      <ContextChips context={message.context} />
      {message.step === "objectiveCodes" ? (
        <p className="chat-guided__hint">Use the message box below. If you do not want to add key objectives, type Skip.</p>
      ) : (
        <div className="chat-guided__options" aria-label={message.question}>
          {options.map((option) => (
            <button key={option} type="button" onClick={() => onSelect(option)}>
              {option === "Pick from list" ? "Pick from list (type codes for now)" : option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GuidedResultMessage({
  message,
  onAction,
  isAnchor,
  latestExchangeRef
}: {
  message: Extract<ChatMessage, { role: "guided_result" }>;
  onAction: (action: string) => void;
  isAnchor: boolean;
  latestExchangeRef: RefObject<HTMLDivElement>;
}) {
  const guidanceResources = getGuidanceResources(message.recommendations);
  return (
    <div className="chat-message chat-message--bot chat-guided" ref={isAnchor ? latestExchangeRef : undefined}>
      <div className="chat-response__meta">
        <span>Get guidance</span>
        <span>{guidanceResources.length} resource{guidanceResources.length === 1 ? "" : "s"} first</span>
      </div>
      <p>Based on your challenge, these are the best places to start.</p>
      <ContextChips context={message.context} />
      {guidanceResources.length > 0 ? (
        <section className="chat-response__section" aria-label="Recommended resources">
          <h3>Recommended resources</h3>
          <div className="chat-resources">
            {guidanceResources.map((resourceRecommendation) => (
              <ResourceResult recommendation={resourceRecommendation} key={resourceRecommendation.resource.id} />
            ))}
          </div>
        </section>
      ) : null}
      {guidanceResources.length > 0 ? (
        <section className="chat-response__section chat-response__why" aria-label="Why these match">
          <h3>Why these match</h3>
          <ul>
            {guidanceResources.map((resourceRecommendation) => (
              <li key={resourceRecommendation.resource.id}>{resourceRecommendation.why}</li>
            ))}
          </ul>
        </section>
      ) : null}
      <section className="chat-response__section" aria-label="Relevant key objectives">
        <h3>Relevant key objectives</h3>
        <div className="chat-objectives">
          {message.recommendations.map((recommendation) => (
            <article className="chat-objective" key={recommendation.objective.id}>
              <div className="chat-objective__head">
                <span>{recommendation.objective.code}</span>
                <strong>{recommendation.objective.title}</strong>
              </div>
              <p>{recommendation.reason}</p>
            </article>
          ))}
        </div>
      </section>
      <div className="chat-widget__chips" aria-label="Guided flow follow-ups">
        {(chatbotConfig.allowFreeText
          ? ["Show resources", "Change challenge", "Change stage", "Change product type", "Add assigned key objectives", "Start over"]
          : ["Change challenge", "Change stage", "Change product type", "Add assigned key objectives", "Start over"]
        ).map((action) => (
          <button key={action} type="button" onClick={() => onAction(action)}>
            {action}
          </button>
        ))}
      </div>
    </div>
  );
}

function ContextChips({ context }: { context: ChatSession["startupContext"] }) {
  const chips = [
    context?.challenge ? `Challenge: ${context.challenge}` : null,
    context?.stage ? `Stage: ${context.stage}` : null,
    context?.productType ? `Product: ${context.productType}` : null,
    context?.assignedObjectiveCodes?.length ? `Assigned key objectives: ${context.assignedObjectiveCodes.join(", ")}` : null
  ].filter(Boolean);

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className="chat-guided__context" aria-label="Selected startup context">
      {chips.map((chip) => (
        <span key={chip}>{chip}</span>
      ))}
    </div>
  );
}

function BotMessage({
  response,
  onFollowUp,
  isAnchor,
  latestExchangeRef,
  isExpanded,
  onShowMore
}: {
  response: ChatbotResponse;
  onFollowUp: (query: string) => void;
  isAnchor: boolean;
  latestExchangeRef: RefObject<HTMLDivElement>;
  isExpanded: boolean;
  onShowMore: () => void;
}) {
  const visibleResources = isExpanded ? response.resources : response.resources.slice(0, 2);
  const extraResourceCount = Math.max(0, response.resources.length - visibleResources.length);
  const sourceMode = response.mode === "api_free_extractive_qa" ? "Source snippet match" : response.mode === "fallback" ? "Needs narrowing" : "Resource recommendation";
  const confidenceLabel = response.confidence === "high" ? "Strong match" : response.confidence === "medium" ? "Possible match" : "Needs narrowing";

  return (
    <div className="chat-message chat-message--bot" ref={isAnchor ? latestExchangeRef : undefined}>
      <div className="chat-response__meta" aria-label="Response metadata">
        <span>Matched from library data</span>
        <span>{confidenceLabel}</span>
        <span>{sourceMode}</span>
      </div>
      <p>{response.answerText}</p>
      <RetrievalTrail response={response} />

      {visibleResources.length > 0 ? (
        <section className="chat-response__section" aria-label="Recommended resources">
          <h3>Recommended resources</h3>
          <div className="chat-resources">
            {visibleResources.map((recommendation) => (
              <ResourceResult recommendation={recommendation} key={recommendation.resource.id} />
            ))}
          </div>
          {extraResourceCount > 0 ? (
            <button className="chat-show-more" type="button" onClick={onShowMore}>
              Show {extraResourceCount} more match{extraResourceCount === 1 ? "" : "es"}
            </button>
          ) : null}
        </section>
      ) : null}

      {response.snippets.length > 0 ? (
        <section className="chat-response__section" aria-label="Relevant library snippets">
          <h3>Relevant library snippets</h3>
          <div className="chat-snippets">
            {response.snippets.slice(0, 3).map((snippet) => (
              <SnippetResult snippet={snippet} key={snippet.chunk.id} />
            ))}
          </div>
        </section>
      ) : null}

      {response.resources.length > 0 ? (
        <section className="chat-response__section chat-response__why" aria-label="Why these matches">
          <h3>Why these matches</h3>
          <ul>
            {visibleResources.map((recommendation) => (
              <li key={recommendation.resource.id}>{recommendation.why}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {response.followUps.length > 0 ? (
        <div className="chat-widget__chips" aria-label="Follow-up prompts">
          {response.followUps.map((followUp) => (
            <button key={followUp} type="button" onClick={() => onFollowUp(followUp)}>
              {followUp}
            </button>
          ))}
        </div>
      ) : null}

      <div className="chat-message__tools">
        <button type="button" onClick={() => copyResponse(response)}>
          <CopyIcon />
          <span>Copy response</span>
        </button>
        <button type="button" onClick={() => onFollowUp("Try broader matches")}>
          Try broader matches
        </button>
      </div>

      <div className="chat-feedback" aria-label="Response feedback">
        <button
          type="button"
          onClick={() => logChatEvent({ eventType: "chatbot_feedback", responseId: response.id, rating: "useful" })}
        >
          Useful
        </button>
        <button
          type="button"
          onClick={() => logChatEvent({ eventType: "chatbot_feedback", responseId: response.id, rating: "not_useful" })}
        >
          Not useful
        </button>
      </div>
    </div>
  );
}

function ResourceResult({ recommendation }: { recommendation: ResourceRecommendation }) {
  const resource = recommendation.resource;
  return (
    <article className="chat-resource">
      <div className="chat-resource__head">
        <div>
          <strong>{resource.name}</strong>
          <div className="chat-resource__meta">
            <span>{resource.format}</span>
            {resource.stages?.slice(0, 2).map((stage) => (
              <span key={stage}>{stage}</span>
            ))}
          </div>
        </div>
        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${resource.name}`}
          onClick={() => logChatEvent({ eventType: "chatbot_click", targetType: "resource", resourceId: resource.id })}
        >
          <ExternalIcon />
        </a>
      </div>
      {resource.summary ? <p>{resource.summary}</p> : null}
      <div className="chat-resource__why">
        <span>Why this was picked</span>
        <small>{recommendation.why}</small>
      </div>
    </article>
  );
}

function SnippetResult({ snippet }: { snippet: SourceSnippet }) {
  return (
    <a
      className="chat-snippet"
      href={snippet.chunk.resourceUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        logChatEvent({
          eventType: "chatbot_click",
          targetType: "snippet",
          chunkId: snippet.chunk.id,
          resourceId: snippet.chunk.resourceId
        })
      }
    >
      <span>{snippet.relevanceLabel}</span>
      <strong>{snippet.chunk.resourceName}</strong>
      <em>{snippet.chunk.heading}</em>
      <q>{snippet.chunk.text}</q>
    </a>
  );
}

function RetrievalTrail({ response }: { response: ChatbotResponse }) {
  const trails = [
    response.matchedTopics[0] ? `Matched topic: ${labelFromId(response.matchedTopics[0])}` : null,
    response.resources[0]?.resource.objectiveIds[0] ? `Matched key objective: ${response.resources[0].resource.objectiveIds[0].replace("objective-", "").replace("-", ".")}` : null,
    response.resources[0]?.resource.enablerIds[0] ? `Matched enabler: ${response.resources[0].resource.enablerIds[0]}` : null
  ].filter(Boolean);

  if (trails.length === 0) {
    return null;
  }

  return (
    <div className="chat-retrieval-trail" aria-label="Retrieval trail">
      {trails.map((trail) => (
        <span key={trail}>{trail}</span>
      ))}
    </div>
  );
}

function copyResponse(response: ChatbotResponse) {
  const lines = [
    response.answerText,
    ...response.resources.map((recommendation, index) => `${index + 1}. ${recommendation.resource.name} - ${recommendation.resource.url}`)
  ];
  navigator.clipboard?.writeText(lines.join("\n"));
}

function createSession(id = `chat-${Date.now()}-${Math.random().toString(16).slice(2)}`): ChatSession {
  const now = new Date().toISOString();
  return {
    id,
    title: "New chat",
    createdAt: now,
    updatedAt: now,
    messages: []
  };
}

function loadSessions(): ChatSession[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "[]") as ChatSession[];
    return trimSessions(parsed.filter((session) => session.id && Array.isArray(session.messages)));
  } catch {
    return [createSession()];
  }
}

function saveSessions(sessions: ChatSession[]) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimSessions(sessions)));
}

function trimSessions(sessions: ChatSession[]): ChatSession[] {
  const cutoff = Date.now() - MAX_SESSION_AGE_MS;
  return sessions
    .filter((session) => new Date(session.updatedAt).getTime() >= cutoff || session.messages.length > 0)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, MAX_SESSIONS);
}

function latestExchangeAnchorId(messages: ChatMessage[]): string | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role === "bot" || message.role === "guided_prompt" || message.role === "guided_result" || message.role === "pending") {
      const previousMessage = messages[index - 1];
      return previousMessage?.role === "user" ? previousMessage.id : message.id;
    }
  }
  return null;
}

function scrollLatestExchangeIntoView(ref: RefObject<HTMLDivElement>) {
  window.setTimeout(() => ref.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 40);
}

function truncateTitle(value: string): string {
  return value.length > 46 ? `${value.slice(0, 43)}...` : value;
}

function randomDelay(min: number, max: number): number {
  return Math.floor(min + Math.random() * Math.max(1, max - min));
}

function formatRelativeDate(value: string): string {
  const diffMs = Date.now() - new Date(value).getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) {
    return "Just now";
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  return `${Math.floor(diffHours / 24)}d ago`;
}

function getStarterGroups() {
  const starters: Array<{ label: string; prompt: string }> = [];

  if (chatbotConfig.allowGuidedFlow) {
    starters.push(
      {
        label: "Get guidance",
        prompt: GUIDED_FLOW_TRIGGER
      },
      {
        label: "Find resources for my challenge",
        prompt: GUIDED_FLOW_TRIGGER
      },
      {
        label: "Use my assigned key objectives",
        prompt: GUIDED_FLOW_TRIGGER
      }
    );
  }

  if (chatbotConfig.allowFreeText) {
    starters.push(
      {
        label: "Validate a problem",
        prompt: "Where should I start with customer problem validation?"
      },
      {
        label: "Find first customers",
        prompt: "What can help me find my first customers?"
      },
      {
        label: "Price the product",
        prompt: "Find resources about pricing and willingness to pay"
      },
      {
        label: "Prepare for fundraising",
        prompt: "I need fundraising and investor readiness resources"
      },
      {
        label: "Improve retention",
        prompt: "Show me resources on retention and customer success"
      }
    );
  }

  return starters;
}

function inputPlaceholder(session: ChatSession, isPending: boolean, hasData: boolean, allowFreeText: boolean): string {
  if (isPending) {
    return "Checking the library...";
  }
  if (session.activeFlow?.id === "get_guidance" && session.activeFlow.step === "objectiveCodes") {
    return "Type key objective codes, e.g. 1.1, 2.3, 3.4";
  }
  if (!allowFreeText) {
    return "Choose a guided option above.";
  }
  if (!hasData) {
    return "Loading the learning guide...";
  }
  return "Ask about startup learning materials...";
}

function labelFromId(value: string): string {
  return value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function guidedStepLabel(step: GuidedFlowPromptMessage["step"]): string {
  if (step === "challenge") {
    return "Step 1 of 4";
  }
  if (step === "stage") {
    return "Step 2 of 4";
  }
  if (step === "productType") {
    return "Step 3 of 4";
  }
  if (step === "assignedObjectives") {
    return "Optional";
  }
  return "Type codes";
}
